"use client";

import { useInfront } from "@/context/infront-context";
import { createInstrumentSlug } from "@/services/infront/utils";
import classNames from "classnames";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

export const INDICIES = [
  {
    displayName: "OMXS30",
    timeZone: "Europe/Stockholm",
    instrument: { feed: 17921, ticker: "OMXS30" },
  },
  {
    displayName: "OMXSPI",
    timeZone: "Europe/Stockholm",
    instrument: { feed: 17921, ticker: "OMXSPI" },
  },
  {
    displayName: "NDX",
    timeZone: "America/New_York",
    instrument: { feed: 2087, ticker: "COMP" },
  },
  {
    displayName: "OMXC25",
    timeZone: "Europe/Copenhagen",
    instrument: { feed: 17665, ticker: "OMXC25" },
  },
  {
    displayName: "USDSEK",
    timeZone: "Europe/Stockholm",
    instrument: { feed: 12, ticker: "USDSEK" },
  },
  {
    displayName: "EURSEK",
    timeZone: "Europe/Stockholm",
    instrument: { feed: 12, ticker: "EURSEK" },
  },
];

const ID = "ticker";

const TickerItem = (props: {
  hasTimeContent: boolean;
  hasPCTContent: boolean;
  index: {
    displayName: string;
    timeZone: string;
    instrument: { feed: number; ticker: string };
  };
}) => {
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    if (!props.hasPCTContent) return;
    const t = setTimeout(() => {
      setFadeIn(true);
    }, 20);
    return () => clearTimeout(t);
  }, [props.hasPCTContent]);

  return (
    <Link
      className="text-sm cursor-pointer flex items-center py-1 space-x-1"
      href={createInstrumentSlug(
        props.index.instrument.feed,
        props.index.instrument.ticker,
      )}
    >
      <span className="whitespace-nowrap font-semibold leading-none">
        {props.index.displayName}
      </span>
      {!props.hasPCTContent && (
        <span className="bg-sand-500 h-[17.65px] md:h-[16.25px] w-16 rounded animate-pulse" />
      )}
      <span
        id={`${ID}-pct-${props.index.instrument.ticker}`}
        className={classNames(
          "font-semibold [&>div]:min-w-16 transition-opacity duration-1000 opacity-0",
          {
            hidden: !props.hasPCTContent,
            "opacity-100": fadeIn,
          },
        )}
      />

      {!props.hasTimeContent && (
        <span className="hidden lg:block min-w-14 animate-pulse">00:00:00</span>
      )}
      <span
        id={`${ID}-time-${props.index.instrument.ticker}`}
        className={classNames("min-w-14", {
          "hidden lg:block": props.hasTimeContent,
          hidden: !props.hasTimeContent,
        })}
      />
    </Link>
  );
};

const Ticker = () => {
  const { isReady, load } = useInfront();
  const [hasPCTContent, setHasPCTContent] = useState<Record<string, boolean>>(
    {},
  );
  const [hasTimeContent, setHasTimeContent] = useState<Record<string, boolean>>(
    {},
  );

  const widgetsRef = useRef<any[]>([]);

  useEffect(() => {
    let active = true;

    load?.(["core"]).then(() => {
      if (!isReady || !active) return;

      widgetsRef.current.forEach((w) => w.destroy());
      widgetsRef.current = [];

      const createWidgets = (instrument: { feed: number; ticker: string }) => {
        const widgetOptions =
          new window.Infront.InstrumentSingleValueWidgetOptions();

        const isFxPair =
          instrument.ticker === "USDSEK" || instrument.ticker === "EURSEK";
        widgetOptions.name = isFxPair ? "LAST" : "PCT_CHANGE";
        if (isFxPair) widgetOptions.decimals = 4;
        widgetOptions.enableChangeStatusColors = true;
        widgetOptions.instrument = instrument;
        widgetOptions.hasContentCallback = () =>
          setHasPCTContent((prev) => ({ ...prev, [instrument.ticker]: true }));

        widgetsRef.current.push(
          window.infront.instrumentSingleValueWidget(
            `#${ID}-pct-${instrument.ticker}`,
            widgetOptions,
          ),
        );

        widgetOptions.name = "TIME";
        widgetOptions.hasContentCallback = () =>
          setHasTimeContent((prev) => ({ ...prev, [instrument.ticker]: true }));

        widgetsRef.current.push(
          window.infront.instrumentSingleValueWidget(
            `#${ID}-time-${instrument.ticker}`,
            widgetOptions,
          ),
        );
      };

      INDICIES.forEach((index) => {
        createWidgets(index.instrument);
      });
    });

    return () => {
      active = false;
      widgetsRef.current.forEach((w) => w.destroy());
      widgetsRef.current = [];
    };
  }, [isReady]);

  return (
    <div className="h-8 bg-white-500 relative flex items-center justify-center">
      <div className="pointer-events-none absolute left-0 top-0 h-full w-4 z-10 bg-gradient-to-r from-white-500 to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 h-full w-4 z-10 bg-gradient-to-l from-white-500 to-transparent" />
      <div className="flex h-6 px-4 items-center md:justify-center space-x-4 sm:space-x-7 overflow-hidden overflow-x-auto hide-scrollbar market-top-row">
        {INDICIES.map((index, i) => (
          <div
            key={i}
            className={classNames({
              "": i > 2,
            })}
          >
            <TickerItem
              hasPCTContent={hasPCTContent[index.instrument.ticker] ?? false}
              hasTimeContent={hasTimeContent[index.instrument.ticker] ?? false}
              index={index}
              key={`${ID}-${index.instrument.ticker.toLowerCase()}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Ticker;
